/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import Image from 'next/image'
import { Text } from '@hashicorp/mds-react'
import s from './style.module.css'

export interface PartnersProps {
	heading: string
	content: string
	logos: Array<{
		url: string
		alt: string
		width: number
		height: number
		linkUrl: string
	}>
}

export default function Partners({ heading, content, logos }) {
	return (
		<div className={s.root}>
			<div className={s.inner}>
				<div className={s.text}>
					<Text.DisplayExpressive tag="h2" className={s.heading}>
						{heading}
					</Text.DisplayExpressive>
					<Text.Body color={'faint'}>{content}</Text.Body>
				</div>
				<LogoGrid items={logos} />
			</div>
		</div>
	)
}

function parseLogoGridItems(items) {
	return items.map((item) => {
		const { linkUrl, url, width, height, ...restItemProps } = item
		const GridItem = () => (
			<Image
				src={url}
				width={width}
				height={height}
				alt={restItemProps.alt || ''}
				{...restItemProps}
			/>
		)
		return {
			linkUrl,
			GridItem,
		}
	})
}

function LogoGrid({ items }) {
	const parsedItems = parseLogoGridItems(items)
	const imgCount = parsedItems.length
	const isBrokenLayout = imgCount % 3 !== 0 || imgCount > 9
	if (isBrokenLayout) {
		let err = `<LogoGrid /> was passed ${imgCount} items, `
		err += `which would result in a broken layout. `
		err += `There must be exactly 3, 6, or 9 items.`
		throw new Error(err)
	}
	return (
		<div className={s.grid}>
			{parsedItems.map((logoGridItem, stableIdx) => {
				const { linkUrl, GridItem } = logoGridItem
				const ItemWrapper = linkUrl ? 'a' : 'div'
				return (
					// eslint-disable-next-line react/no-array-index-key
					<div key={stableIdx} className={s.item}>
						<div className={s.itemAspect}>
							<ItemWrapper
								href={linkUrl || undefined}
								className={s.itemAspectInner}
							>
								<GridItem />
							</ItemWrapper>
						</div>
					</div>
				)
			})}
		</div>
	)
}
